// src/admin/components/common/CollapsibleCard.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * A card component with collapsible content that supports lazy loading.
 * 
 * @param {React.ReactNode} header - The always-visible header content
 * @param {React.ReactNode} children - The collapsible content (rendered when expanded)
 * @param {boolean} defaultExpanded - Whether to start expanded (default: false)
 * @param {function} onExpand - Called when the card is expanded (useful for lazy loading)
 * @param {boolean} isLoading - Shows loading spinner in content area
 * @param {boolean} isEmpty - Shows empty state message
 * @param {string} emptyMessage - Message to show when isEmpty is true
 * @param {string} className - Additional CSS classes for the card
 * @param {string} variant - 'default' or 'table' (table removes body padding for edge-to-edge tables)
 */
export default function CollapsibleCard({
  header,
  children,
  defaultExpanded = false,
  onExpand,
  isLoading = false,
  isEmpty = false,
  emptyMessage = 'No items to display.',
  className = '',
  variant = 'default',
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Call onExpand when expanded changes to true
  useEffect(() => {
    if (expanded && onExpand) {
      onExpand();
    }
  }, [expanded, onExpand]);

  const toggleExpanded = () => {
    setExpanded(prev => !prev);
  };

  const isTableVariant = variant === 'table';

  return (
    <div className={classNames('card mb-3', className)}>
      {/* Header - always visible */}
      <div
        className="card-header bg-white d-flex align-items-center justify-content-between"
        style={{ cursor: 'pointer' }}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className="flex-grow-1">{header}</div>
        <i
          className={classNames(
            'bi fs-5 text-muted ms-2',
            expanded ? 'bi-chevron-up' : 'bi-chevron-down'
          )}
        />
      </div>

      {/* Collapsible content */}
      {expanded && (
        <div className={classNames({
          'card-body': !isTableVariant,
          'card-body px-0 py-0': isTableVariant,
        })}>
          {isLoading ? (
            <div className={classNames('text-center py-4', { 'px-3': isTableVariant })}>
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2 mb-0">Loading...</p>
            </div>
          ) : isEmpty ? (
            <div className={classNames('text-center py-4 text-muted', { 'px-3': isTableVariant })}>
              <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
              <p className="mb-0">{emptyMessage}</p>
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}

CollapsibleCard.propTypes = {
  header: PropTypes.node.isRequired,
  children: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  onExpand: PropTypes.func,
  isLoading: PropTypes.bool,
  isEmpty: PropTypes.bool,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'table']),
};
